import { ReactElement, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { red } from '@mui/material/colors';
import Fade from '@mui/material/Fade';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useField, useFormikContext } from 'formik';
import { NAVIGATION_ACTIONS } from 'pages/AnnualUserAccessReview/_const';
import { checkForExistingIdentifier } from 'pages/NewNonEmployee/_api';
import {
	DUPLICATE_REASONS,
	NEW_NON_EMPLOYEE_KEYS,
} from 'pages/NewNonEmployee/_const';
import CustomCheckbox from '../base/CustomCheckbox';
import CustomTextField from '../base/CustomTextField';
import { getLegacyPath, getPath } from 'routes/_utils';
import { setSession } from 'store/utility';
import { extractIdentifierData } from 'pages/NewNonEmployee/_util';

/**
 * set session variables to be used downstream by legacy page after redirect
 * @param {object[]} values key-value pairs to set in Session
 */
const setCommonSessionVariables = async values => {
	await setSession('UserType', values['UserType']);
	await setSession('UserTypePrimaryId', values['UserTypePrimaryId']);
	const fn = (values[NEW_NON_EMPLOYEE_KEYS.FIRST_NAME] ?? '').replace(
		"'",
		'`'
	);
	const ln = (values[NEW_NON_EMPLOYEE_KEYS.LAST_NAME] ?? '').replace(
		"'",
		'`'
	);
	await setSession('NewID_FirstName', fn);
	await setSession('NewID_LastName', ln);
	await setSession('strDOB', values[NEW_NON_EMPLOYEE_KEYS.DATE_OF_BIRTH]);
	await setSession(
		'IdentifierType',
		values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE]
	);
	await setSession('newTrackingNumMod', 'firstcall');
};

/**
 * Reusable link back to home
 */
const HomeLink = (
	<>
		Click{' '}
		<Link href='/index.html' underline='always'>
			here
		</Link>{' '}
		to return to the main menu.
	</>
);

/**
 * Reusable link back to previous back
 * @returns link taking user back to previous step
 */
const BackLink = ({ onNavigate, altLabel }) => {
	const label = altLabel ? (
		<> to go back to the previous step and edit the form.</>
	) : (
		<>
			to go back to the previous step and edit the{' '}
			<strong>identifier type and/or number</strong> you provided on the
			form.
		</>
	);
	return (
		<>
			Click{' '}
			<Link
				onClick={() => onNavigate()}
				underline='always'
				component='button'
			>
				here
			</Link>{' '}
			{label}
		</>
	);
};

/**
 * Reusable link to the Agreements page
 * @param {string|number} userInfoFiId T_ESO.FI_ID
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns link redirecting to the Agreements page
 */
const AgreementLink = ({ userInfoFiId, extras = {} }) => {
	const history = useHistory();
	const { values } = useFormikContext();

	return (
		<>
			Click{' '}
			<Link
				onClick={async () => {
					await setCommonSessionVariables({ ...values, ...extras });

					await setSession('id', userInfoFiId);
					history.push(
						getPath(`/Agreement/Index?ID=${userInfoFiId}&privs=1`)
					);
				}}
				underline='always'
				component={'button'}
			>
				here
			</Link>{' '}
			if you would like to Add/Modify/Revoke application account access
			for this identity.
		</>
	);
};

/**
 * Reusable button to the Agreements page
 * @param {string|number} userInfoFiId T_ESO.FI_ID
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns button redirecting to the Agreements page
 */
const AgreementButton = ({ userInfoFiId, extras = {} }) => {
	const history = useHistory();
	const { values } = useFormikContext();

	return (
		<Button
			variant='contained'
			onClick={async () => {
				await setCommonSessionVariables({ ...values, ...extras });

				await setSession('id', userInfoFiId);
				await setSession('ESOID', userInfoFiId);

				history.push(
					getLegacyPath(
						`/ESO/UpdateEsoAcct.aspx?id=${userInfoFiId}&nextlink=../Agreement/Index?ID=${userInfoFiId}`
					)
				);
			}}
		>
			Add/Modify/Revoke application account access for this identity
		</Button>
	);
};

/**
 *
 * @param {object} loggedInUser currently logged in user
 * @param {function} onNavigate helper function to enable stepper navigation
 * @param {string} name identifier to be used for access to the Formik Context
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns {ReactElement} dynamic display of duplicate identity checks
 */
const DuplicateIdentity = ({
	loggedInUser,
	onNavigate,
	extras = {},
	...props
}) => {
	const { values, setFieldValue } = useFormikContext();
	const [field, , helpers] = useField(props);
	const [sDupUsers, setDupUsers] = useState([]);

	//watch for trigger to transition to next step and wait to allow user to see the message
	const triggerTransition = () => {
		const timer = setTimeout(() => {
			onNavigate(NAVIGATION_ACTIONS.NEXT);
		}, 2000);
		return () => {
			clearTimeout(timer);
		};
	};

	useEffect(() => {
		let active = true;

		async function fetchApi() {
			const { data } = await checkForExistingIdentifier(
				values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_NUMBER],
				values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE],
				values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_SPECIFIER],
				values[NEW_NON_EMPLOYEE_KEYS.LAST_NAME],
				values[NEW_NON_EMPLOYEE_KEYS.DATE_OF_BIRTH],
				loggedInUser
			);

			setDupUsers([...data.duplicateAccounts]);

			helpers.setValue(data.reason);
			helpers.setTouched(true);
		}

		if (active && values) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [values, loggedInUser, helpers]);

	const LoadingAnimation = <CircularProgress size={20} />;
	const commonProps = {
		userInfo: sDupUsers[0],
		onGoBack: () => {
			onNavigate(NAVIGATION_ACTIONS.PREVIOUS);
			setFieldValue(
				NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK,
				DUPLICATE_REASONS.LOADING
			);
		},
		extras: { ...extras },
	};

	switch (field.value) {
		case DUPLICATE_REASONS.ESO_FOUND_SAME_AS_REQUESTOR:
			return <SelfRequest {...commonProps} />;
		case DUPLICATE_REASONS.ESO_FOUND:
			return <ESOExists {...commonProps} />;
		case DUPLICATE_REASONS.IDENTITY_FOUND_NO_ESO:
			return <IdentityExists {...commonProps} />;
		case DUPLICATE_REASONS.SUSPECTED_DUPLICATE_ESO:
			return (
				<DuplicateIdentityVerification
					{...commonProps}
					userInfo={sDupUsers}
				/>
			);
		case DUPLICATE_REASONS.ESO_FOUND_RETIRED:
			return <RetiredIdentityExists {...commonProps} />;
		case DUPLICATE_REASONS.NONE:
			triggerTransition();
			return (
				<Fade in={true}>
					<Stack direction={'column'} gap={5} alignItems={'center'}>
						<VerifiedIcon
							color={'success'}
							sx={{ transform: 'scale(4)' }}
						/>
						<Typography variant={'h5'}>
							Confirmed the identity does not already exist
						</Typography>
					</Stack>
				</Fade>
			);
		case DUPLICATE_REASONS.FORM_INCOMPLETE:
			return (
				<Stack direction={'row'} gap={1} alignItems={'center'}>
					<Typography>
						Please ensure you have filled out the form in the{' '}
						<Link
							onClick={() =>
								onNavigate(NAVIGATION_ACTIONS.PREVIOUS)
							}
							underline='always'
							component='button'
						>
							previous step
						</Link>{' '}
						correctly and try again.
					</Typography>
				</Stack>
			);
		case DUPLICATE_REASONS.INVALID_IDENTIFIER:
			return (
				<Stack direction={'row'} gap={1} alignItems={'center'}>
					<Typography>
						Please provide a valid identifier. Placeholder or non-genuine SSN patterns are not allowed. If the user declines to provide personal identifiers, select Microsoft ID and provide a 6-7 digit user-known number.
					</Typography>
				</Stack>
			);
		case DUPLICATE_REASONS.LOADING:
		default:
			return (
				<Stack direction={'row'} gap={1} alignItems={'center'}>
					{LoadingAnimation}
					<Typography>
						Verifying the identity information provided does not
						match any existing records
					</Typography>
				</Stack>
			);
	}
};

/**
 * If duplicate identity detected is the same as the requestor
 * @param {function} onGoBack helper function to enable stepper navigation
 * @returns {ReactElement} Duplicate Identity type: self request
 */
const SelfRequest = ({ onGoBack }) => {
	return (
		<DuplicateView
			alertMessage={
				'The identity you are attempting to create conflicts with your own identity information.'
			}
			contents={[<BackLink onNavigate={onGoBack} />, HomeLink]}
		/>
	);
};

/**
 * If duplicate identity detected suspicion of duplicate (last name + DOB)
 * @param {object} userInfo information about the duplicates
 * @param {function} onGoBack helper function to enable stepper navigation
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns {ReactElement} Duplicate Identity type: suspected duplicate
 */
const DuplicateIdentityVerification = ({ userInfo, onGoBack, extras = {} }) => {
	return (
		<DuplicateView
			alertMessage={
				<>
					One or more identities with the same last name and date of
					birth already exists in IMS, as listed below. If the person
					listed below is not the same person you intended to add to
					IMS, acknowledge and provide a reason below. You will not be
					able to proceed otherwise.
				</>
			}
			contents={[
				<>
					{userInfo.map((ui, index) => (
						<MetadataCard
							metadata={ui}
							defaultOpen={index === 0}
							extras={extras}
						/>
					))}
					<Stack gap={1}>
						<Typography>
							If you believe the user information you have
							provided does not match the user(s) above, please
							acknowledge by checking the box and providing a
							reason in the textbox below and then click the
							submit button.
						</Typography>
						<Typography color='error'>
							Note: Creating duplicate IDs is against UPMC policy
							and may result in revocation of your IMS request
							privileges
						</Typography>
						<Box sx={{ ml: 2, mr: 2 }}>
							<CustomCheckbox
								name={
									NEW_NON_EMPLOYEE_KEYS.NOT_A_DUPLICATE_ACKNOWLEDGEMENT
								}
								label='I acknowledge that the information I have provided is not a duplicate identity matching the account information listed above.'
								required
							/>
							<CustomTextField
								name={
									NEW_NON_EMPLOYEE_KEYS.NOT_A_DUPLICATE_REASON
								}
								label='Reason'
								required
							/>
						</Box>
					</Stack>
				</>,
				<Divider />,
				<BackLink onNavigate={onGoBack} altLabel />,
				HomeLink,
			]}
		/>
	);
};

/**
 * Display user information
 * @param {object} metadata user information
 * @param {boolean} defaultOpen flag to indicate initial open behavior
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns {ReactElement} Accordion displaying user information
 */
const MetadataCard = ({ metadata, defaultOpen = true, extras = {} }) => {
	const [expanded, setExpanded] = useState(defaultOpen);
	const valueOrDefault = (value, isDate = false) => {
		if (value) {
			if (isDate) {
				return new Date(value).toISOString().split('T')[0];
			}
			return value;
		} else {
			return 'N/A';
		}
	};

	const getInitials = (firstName, lastName) => {
		if (firstName && lastName) {
			const firstInitial = firstName.trim()[0];
			const secondInitial = lastName.trim()[0];
			return `${firstInitial}${secondInitial}`;
		} else {
			return '??';
		}
	};

	const concatName = (f, l, m) => {
		if (f && l) {
			if (m) {
				return `${l}, ${f} ${m}`;
			} else {
				return `${l}, ${f}`;
			}
		}
		return 'N/A';
	};

	return (
		<Accordion
			sx={{ mb: 2 }}
			expanded={expanded}
			onChange={() => setExpanded(!expanded)}
		>
			<AccordionSummary expandIcon={<ExpandLessIcon />}>
				<CardHeader
					avatar={
						<Avatar
							sx={{ bgcolor: red[500] }}
							aria-label='profile-avatar'
						>
							{getInitials(metadata.firstName, metadata.lastName)}
						</Avatar>
					}
					title={valueOrDefault(
						concatName(
							metadata?.firstName,
							metadata?.lastName,
							metadata?.middleInitial
						)
					)}
					subheader={metadata.jobTitle}
				/>
			</AccordionSummary>
			<AccordionDetails>
				<MetadataInfo
					title='Date of Birth'
					value={valueOrDefault(metadata?.dateOfBirth, true)}
				/>
				<MetadataInfo
					title='Department'
					value={valueOrDefault(metadata?.departmentName)}
				/>
				<MetadataInfo
					title='Manager Email'
					value={valueOrDefault(metadata?.supervisorEmail)}
				/>
				<MetadataInfo
					title='ESO ID'
					value={valueOrDefault(metadata?.familiarEsoId)}
				/>
			</AccordionDetails>
			<AccordionActions>
				<AgreementButton userInfoFiId={metadata.fiId} extras={extras} />
			</AccordionActions>
		</Accordion>
	);
};

/**
 * Utility component to display key-value pairs
 * @param {string} title
 * @param {string} value
 * @returns {ReactElement} if a value exists, display the key-value pairs
 */
const MetadataInfo = ({ title, value }) => {
	return value ? (
		<Typography color='text.secondary'>
			{title}: {value}
		</Typography>
	) : null;
};

/**
 * If duplicate identity detected is a ESO-exists
 * @param {object} userInfo information about the duplicates
 * @param {function} onGoBack helper function to enable stepper navigation
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns {ReactElement} Duplicate Identity type: ESO Exists
 */
const ESOExists = ({ userInfo, onGoBack, extras = {} }) => {
	const { values } = useFormikContext();
	const [type, number, specifier] = extractIdentifierData(values);

	return (
		<DuplicateView
			alertMessage={
				<>
					<Typography>
						The identity you are attempting to create already exists
						and has an E-Sign-On associated with it.
					</Typography>
					<Typography sx={{ mt: 1 }}>
						{type}: {number} {specifier && `(${specifier})`}
					</Typography>
				</>
			}
			contents={[
				<AgreementLink userInfoFiId={userInfo.fiId} extras={extras} />,
				<Divider />,
				<BackLink onNavigate={onGoBack} />,
				HomeLink,
			]}
		/>
	);
};

/**
 * If duplicate identity detected is a Identity-exists (no ESO)
 * @param {object} userInfo information about the duplicates
 * @param {function} onGoBack helper function to enable stepper navigation
 * @returns {ReactElement} Duplicate Identity type: Identity Exists
 */
const IdentityExists = ({ userInfo, onGoBack, extras = {} }) => {
	const history = useHistory();
	const { values } = useFormikContext();
	const [type, number, specifier] = extractIdentifierData(values);

	return (
		<DuplicateView
			alertMessage={
				<>
					<Typography>
						The identity you are attempting to create already
						exists, but does not have an E-Sign-On associated with
						it.
					</Typography>

					<Typography sx={{ mt: 1 }}>
						{type}: {number} {specifier && `(${specifier})`}
					</Typography>
				</>
			}
			contents={[
				<>
					Click{' '}
					<Link
						onClick={async () => {
							await setCommonSessionVariables({
								...values,
								...extras,
								...{
									[NEW_NON_EMPLOYEE_KEYS.DATE_OF_BIRTH]:
										userInfo.dateOfBirth,
									[NEW_NON_EMPLOYEE_KEYS.FIRST_NAME]:
										userInfo.firstName,
									[NEW_NON_EMPLOYEE_KEYS.LAST_NAME]:
										userInfo.lastName,
								},
							});
							history.push(
								getLegacyPath(
									`/ESO/NewEso.ASPX?id=${userInfo.iamiId}&optn=click`
								)
							);
						}}
						underline='always'
						component={'button'}
					>
						here
					</Link>{' '}
					to create a new UPMC E-Sign-On for this identity.
				</>,
				<Divider />,
				<BackLink onNavigate={onGoBack} />,
				HomeLink,
			]}
		/>
	);
};

/**
 * If duplicate identity detected is a retired account
 * @param {object} userInfo information about the duplicates
 * @param {function} onGoBack helper function to enable stepper navigation
 * @param {object} extras additional key-value pairs to with extra metadata
 * @returns {ReactElement} Duplicate Identity type: retired
 */
const RetiredIdentityExists = ({ userInfo, onGoBack, extras = {} }) => {
	const history = useHistory();
	const { values } = useFormikContext();
	const [type, number, specifier] = extractIdentifierData(values);

	return (
		<DuplicateView
			alertMessage={
				<>
					<Typography>
						The identity you are attempting to create already exists
						and is associated with a retired account.
					</Typography>

					<Typography sx={{ mt: 1 }}>
						{type}: {number} {specifier && `(${specifier})`}
					</Typography>
				</>
			}
			contents={[
				<>
					Click{' '}
					<Link
						onClick={async () => {
							await setCommonSessionVariables({
								...values,
								...extras,
							});
							await setSession('id', userInfo.fiId);
							await setSession('ESOID', userInfo.fiId);
							history.push(
								getLegacyPath('/ESO/Reactivate_View.aspx')
							);
						}}
						underline='always'
						component={'button'}
					>
						here
					</Link>{' '}
					if you would like to reactivate account access for this
					identity.
				</>,
				<Divider />,
				<BackLink onNavigate={onGoBack} />,
				HomeLink,
			]}
		/>
	);
};

/**
 * View for duplicate identity displays
 * @param {string} alertMessage message to display in the alert
 * @param {ReactElement[]} contents list of elements to display (typically navigation elements)
 * @returns {ReactElement} template for duplicate identity view
 */
const DuplicateView = ({ alertMessage, contents = [] }) => {
	return (
		<Card variant='outlined' sx={{ minHeight: 150, m: 1, flexGrow: 1 }}>
			<CardContent>
				<Alert severity={'warning'}>
					<Typography>{alertMessage}</Typography>
				</Alert>
			</CardContent>
			{contents.map((content, index) => (
				<CardContent key={index}>
					<Typography variant='subtitle2'>{content}</Typography>
				</CardContent>
			))}
		</Card>
	);
};

export default DuplicateIdentity;
